import { useEffect, useRef, useState } from 'react';

/**
 * Ease-out cubic. Fast at the start, settling gently into the target — the
 * "feeling rather than a jarring visual change" the progress-bar spec asks for.
 */
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Ease-in-out cubic, for the paused/playing choreography where the figure both
 * departs and arrives and a one-sided ease reads lopsided.
 */
export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/**
 * Drives a number toward `target` over `durationMs`, re-easing from wherever it
 * currently sits whenever the target changes.
 *
 * SVG path data cannot be transitioned by CSS, and the merged bar/chevron
 * silhouette changes *shape* between paused and playing rather than just
 * transforming — so the layout scalars are tweened here and the path is rebuilt
 * per frame from the eased values.
 *
 * Respects `prefers-reduced-motion` by snapping straight to the target.
 */
export function useTween(
  target: number,
  durationMs: number,
  easing: (t: number) => number = easeInOutCubic,
): number {
  const [value, setValue] = useState(target);
  // Read in the animation loop only, so changing them mid-flight never forces
  // a re-render or restarts the frame callback.
  const fromRef = useRef(target);
  const startedAtRef = useRef<number | null>(null);
  const valueRef = useRef(target);

  useEffect(() => {
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || durationMs <= 0) {
      valueRef.current = target;
      setValue(target);
      return;
    }

    fromRef.current = valueRef.current;
    startedAtRef.current = null;
    let frame = 0;

    const step = (now: number) => {
      if (startedAtRef.current === null) startedAtRef.current = now;
      const elapsed = now - startedAtRef.current;
      const progress = Math.min(elapsed / durationMs, 1);
      const next = fromRef.current + (target - fromRef.current) * easing(progress);

      valueRef.current = next;
      setValue(next);

      if (progress < 1) frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs, easing]);

  return value;
}
